/* ============================================================
   PA • Wholesale Form
   Client-only form handler — no Shopify contact-form POST.
   Submits a JSON payload via fetch() to a merchant-configured
   webhook URL (section setting: data-webhook-url), so it can
   be pointed at any external endpoint (e.g. an automation
   server) to create tasks, sync a CRM/list, and send email.

   POST payload shape — matches Hermes's WholesaleForm schema
   exactly (see /openapi.json on the webhook host):
   {
     "businessName": string,
     "contact": string,          // contact name
     "email": string,
     "phone": string,
     "businessType": string,
     "volume": string,
     "packInterest": boolean,    // true unless "Not sure yet" / blank
     "customLogoInterest": boolean,
     "message": string,          // includes the specific pack chosen,
                                  // since Hermes only takes a boolean
     "sourcePage": string,
     "timestamp": string (ISO 8601)
   }
   ============================================================ */
(function () {
  'use strict';

  function PAWholesaleForm(root) {
    this.root = root;
    this.webhookUrl = root.dataset.webhookUrl || '';
    this.form = root.querySelector('[data-wholesale-form-el]');
    this.card = root.querySelector('[data-wholesale-form-card]');
    this.success = root.querySelector('[data-wholesale-form-success]');
    this.errorEl = root.querySelector('[data-wholesale-form-error]');
    this.submitBtn = this.form ? this.form.querySelector('[type="submit"]') : null;

    if (this.form) this._bind();
  }

  PAWholesaleForm.prototype._bind = function () {
    var self = this;
    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      self._handleSubmit();
    });
  };

  PAWholesaleForm.prototype._setError = function (message) {
    if (!this.errorEl) return;
    if (message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    } else {
      this.errorEl.hidden = true;
    }
  };

  PAWholesaleForm.prototype._handleSubmit = function () {
    var self = this;
    var form = this.form;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Honeypot — a filled hidden field means a bot filled every input.
    // Pretend success without ever sending the data anywhere.
    var honeypot = form.elements['website'];
    if (honeypot && honeypot.value) {
      this._showSuccess();
      return;
    }

    if (!this.webhookUrl) {
      this._setError('This form isn’t connected yet — add a webhook URL in the section settings.');
      return;
    }

    var data = new FormData(form);

    // Hermes only has a boolean "packInterest" field, not a specific
    // pack choice — fold the actual selection into the message so
    // sales doesn't lose it, and derive the boolean from whether the
    // merchant picked a real pack vs. "Not sure yet" / left it blank.
    var packChoice = data.get('interested_pack') || '';
    var packInterest = packChoice !== '' && packChoice.toLowerCase().indexOf('not sure') === -1;
    var messageParts = [];
    if (packChoice) messageParts.push('Interested pack: ' + packChoice);
    if (data.get('message')) messageParts.push(data.get('message'));

    var payload = {
      businessName: data.get('business_name') || '',
      contact: data.get('contact_name') || '',
      email: data.get('email') || '',
      phone: data.get('phone') || '',
      businessType: data.get('business_type') || '',
      volume: data.get('monthly_volume') || '',
      packInterest: packInterest,
      customLogoInterest: data.get('custom_logo_interest') === 'Yes',
      message: messageParts.join('\n\n'),
      sourcePage: window.location.href,
      timestamp: new Date().toISOString()
    };

    this._setError('');
    if (this.submitBtn) {
      this.submitBtn.disabled = true;
      this.submitBtn.dataset.originalLabel = this.submitBtn.dataset.originalLabel || this.submitBtn.textContent;
      this.submitBtn.textContent = 'Submitting…';
    }

    fetch(this.webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Request failed with status ' + res.status);
        self._showSuccess();
      })
      .catch(function (err) {
        console.error('[PAWholesaleForm]', err);
        self._setError('Something went wrong sending your application. Please try again, or email us directly.');
        if (self.submitBtn) {
          self.submitBtn.disabled = false;
          self.submitBtn.textContent = self.submitBtn.dataset.originalLabel;
        }
      });
  };

  PAWholesaleForm.prototype._showSuccess = function () {
    if (this.card) this.card.hidden = true;
    if (this.success) {
      this.success.hidden = false;
      this.success.setAttribute('tabindex', '-1');
      this.success.focus();
    }
  };

  function boot() {
    document.querySelectorAll('[data-wholesale-form]').forEach(function (el) {
      new PAWholesaleForm(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
